/**
 * IT Admin → IT training (`/it-admin/training`, 8 Oct): the incident SLA
 * reference on top (22 Oct, Rusty), then "What's new", then the lessons by
 * topic, with a search box. For admins and IT teams
 * (`<ItAdminLayout>` checks `it_admin`). The lessons are files: see
 * `lib/lessons.ts` for the format and `content.ts` for where they live.
 */

import { Icon, Megaphone, Search } from "@norican/icons";
import { Badge, Card, Input, Typography } from "@norican/react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { SectionCard } from "@/components/common";
import { useLanguage, useT } from "@/i18n";

import { LESSON_INDEX } from "./content";
import {
  filterLessons,
  groupByTopic,
  type LessonEntry,
  listLessons,
  SOURCE_LANGUAGE,
  type TrainingTopic,
  whatsNewFor,
} from "./lib/lessons";
import { EnglishOnlyNote, TOPIC_ICONS, useContentDate } from "./shared";
import { SlaReferenceCard } from "./sla-reference-card";

function LessonCard({ entry }: { entry: LessonEntry }) {
  const { t } = useT("tickets");
  const formatDate = useContentDate();
  const { lesson } = entry;
  return (
    <Card variant="outline" padding="md" interactive className="relative flex flex-col gap-2">
      <Typography as="h3" variant="heading-6" lang={lesson.lang}>
        <Link
          to={`/it-admin/training/${lesson.slug}`}
          className="after:absolute after:inset-0 after:content-[''] focus-visible:outline-none"
        >
          {lesson.title}
        </Link>
      </Typography>
      <Typography as="p" variant="body-sm" tone="muted" lang={lesson.lang} className="text-pretty">
        {lesson.summary}
      </Typography>
      <span className="mt-auto flex flex-wrap items-center gap-2">
        {lesson.status === "draft" ? (
          <Badge tone="warning" variant="subtle" size="sm">
            {t("itAdmin.training.draft")}
          </Badge>
        ) : null}
        <Typography as="span" variant="caption" tone="muted">
          {t("itAdmin.training.updated", { date: formatDate(lesson.updated) })}
        </Typography>
      </span>
    </Card>
  );
}

function WhatsNew({ lang }: { lang: string }) {
  const { t } = useT("tickets");
  const formatDate = useContentDate();
  const entries = whatsNewFor(LESSON_INDEX, lang);
  if (entries.length === 0) {
    return null;
  }
  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-2">
          <Icon icon={Megaphone} size="sm" tone="accent" />
          {t("itAdmin.training.whatsNew")}
        </span>
      }
      accentTone="accent"
    >
      <ul className="flex flex-col gap-2">
        {entries.map((entry) => (
          <li
            key={`${entry.date}-${entry.slug}-${entry.text}`}
            className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-3"
          >
            <Badge tone="neutral" variant="subtle" size="sm" className="shrink-0 self-start">
              <time dateTime={entry.date}>{formatDate(entry.date)}</time>
            </Badge>
            <Link
              to={`/it-admin/training/${entry.slug}`}
              className="text-accent-fg-subtle text-sm underline-offset-2 hover:underline"
            >
              {entry.text}
            </Link>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

export function ItTrainingPage() {
  const { t } = useT("tickets");
  const { language } = useLanguage();
  const [query, setQuery] = useState("");

  const all = useMemo(() => listLessons(LESSON_INDEX, language), [language]);
  const topicLabel = (topic: TrainingTopic) => t(`itAdmin.training.topic.${topic}`);
  const shown = filterLessons(all, query, topicLabel);
  const groups = groupByTopic(shown);
  const searching = query.trim() !== "";

  return (
    <div className="flex flex-col gap-6">
      <SlaReferenceCard />

      <div className="flex flex-col gap-2">
        <Typography as="p" variant="body" className="text-pretty">
          {t("itAdmin.training.intro")}
        </Typography>
        {language === SOURCE_LANGUAGE ? null : <EnglishOnlyNote />}
      </div>

      <Input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value.slice(0, 100))}
        placeholder={t("itAdmin.training.searchPlaceholder")}
        aria-label={t("itAdmin.training.search")}
        startSlot={<Icon icon={Search} size="sm" />}
        className="w-full sm:max-w-md"
      />

      {searching ? null : <WhatsNew lang={language} />}

      {groups.length === 0 ? (
        <Typography as="p" variant="body" tone="muted" role="status">
          {t("itAdmin.training.noResults", { query: query.trim() })}
        </Typography>
      ) : (
        groups.map(({ topic, entries }) => (
          <section key={topic} className="flex flex-col gap-3" aria-labelledby={`topic-${topic}`}>
            <Typography
              as="h2"
              variant="heading-5"
              id={`topic-${topic}`}
              className="flex items-center gap-2"
            >
              <Icon icon={TOPIC_ICONS[topic]} size="sm" tone="accent" />
              {topicLabel(topic)}
            </Typography>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {entries.map((entry) => (
                <LessonCard key={entry.lesson.slug} entry={entry} />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
